import { Button, Icon, Input, Notification, Typography } from '@smartgift/frontend-toolkit';
import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { CardContent } from '@hero/atoms';
import {
  CONTACT_TYPE,
  NON_EDITABLE_RECOGNITION_STATUS,
  PAYMENT_METHOD_TYPES
} from '@hero/constants';
import { useMaxContactHasError } from '@hero/hooks';
import { DetailItem } from '@hero/molecules';
import {
  BasicRecipientTable,
  ContactsEditModal,
  WithoutContactRecipientVerification
} from '@hero/organisms';
import {
  useGetRecognition,
  useGetRecognitionRecipientList,
  useRecogRecipientsBulkUpsert,
  useRecogRecipientsDelete,
  useUpdateRecognition
} from '@hero/services/order-service';
import { useNotification } from '@hero/store/hooks';
import { removeRecipientsSchema } from '@hero/validations';

import * as S from './style';

const ReviewAndPayContactsAccordion = ({ editableContactList }) => {
  const { t } = useTranslation();
  const { recognitionCode } = useParams();
  const { showNotification, showSuccess } = useNotification();

  const [addingMethod, setAddingMethod] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);

  const { mutateAsync: recipientDelete } = useRecogRecipientsDelete();
  const { mutateAsync: recipientBulkUpsert } = useRecogRecipientsBulkUpsert();
  const { data: recognition, isSuccess: isSuccessRecognition } = useGetRecognition(
    {
      recognitionCode
    },
    {
      onSuccess: data => {
        setAddingMethod(data.contacts?.type);
      }
    }
  );

  const { data: recognitionRecipients } = useGetRecognitionRecipientList(
    {
      code: recognitionCode,
      options: {
        pagination: false,
        select: 'contact.id'
      }
    },
    {
      enabled: !!recognitionCode
    }
  );

  const recognitionRecipientContactIds = useMemo(() => {
    return (
      recognitionRecipients?.docs.map(doc => doc?.contact?.id).filter(contactId => contactId) || []
    );
  }, [recognitionRecipients]);

  const { mutateAsync: updateRecognition } = useUpdateRecognition();
  const [isContactsEditModalOpen, setIsContactsEditModalOpen] = useState(false);
  const [contacts, setContacts] = useState();

  const maxContactHasError = useMaxContactHasError({
    recognition,
    recognitionCode,
    contacts
  });

  const isDetailItemEditable = React.useMemo(
    () => !NON_EDITABLE_RECOGNITION_STATUS.includes(recognition?.status?.current),
    [recognition]
  );

  const updateContacts = async contactIds => {
    await recipientBulkUpsert({
      params: {
        code: recognitionCode
      },
      contactIds,
      recipientContactIds: recognitionRecipientContactIds
    });

    await updateRecognition({
      params: { recognitionCode },
      body: {
        contacts: {
          type: 'list'
        }
      },
      successMessage: t('recipientsUpdatedSuccessfully')
    });
  };

  useEffect(() => {
    if (recognition?.contacts) {
      setContacts(recognition?.contacts);
    }
  }, [recognition?.contacts]);

  const addingMethods = useMemo(() => {
    const orderType = 'oneTimeGift';

    return {
      [CONTACT_TYPE.list]: {
        badge: !recognitionRecipients?.totalDocs
          ? t('badgeSelectAtLeastOneContactToActivate', { orderType })
          : ''
      },
      [CONTACT_TYPE.number]: {
        badge: !recognition?.contacts?.maxCount
          ? t('badgeSpecifyRecipientQuantityToActivate', { orderType })
          : ''
      }
    };
  }, [recognitionRecipients, recognition?.contacts?.maxCount]);

  const onDeleteModal = (recipient, onSuccess) => {
    showNotification({
      title: t('areYouSureRemoveRecipient'),
      message: t('areYouSureRemoveRecipientMessage', { feature: 'oneTimeGift' }),
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      secondaryButtonProps: { children: t('cancel') },
      primaryButtonProps: {
        color: 'error',
        children: t('yesRemove'),
        onClick: async () => {
          await recipientDelete({
            code: recognitionCode,
            contactRefIds: [recipient.contact.id]
          });

          showSuccess({
            message: (
              <Trans count={1} i18nKey="recipientSuccessfullyRemoved" components={{ b: <b /> }} />
            ),
            type: 'toast'
          });

          if (onSuccess) onSuccess();
        }
      }
    });
  };

  const onDeleteMultiModal = ({ onSuccess = () => {} }) => {
    showNotification({
      title: t('areYouSureRemoveRecipients'),
      message: t('areYouSureRemoveRecipientsMessage'),
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      formData: [
        {
          name: 'confirm',
          title: (
            <Typography variant="body2Bold" style={{ marginBottom: 4 }}>
              {t('removeRecipientsModalConfirmLabel')}
            </Typography>
          ),
          type: 'text'
        }
      ],
      formValidationSchema: removeRecipientsSchema,
      secondaryButtonProps: { children: t('cancel') },
      primaryButtonProps: {
        color: 'error',
        children: t('yesRemove'),
        onClick: async () => {
          const contactIds = Object.keys(selectedRows);
          await recipientDelete({
            code: recognitionCode,
            contactRefIds: contactIds
          });
          onSuccess();
          showSuccess({
            message: (
              <Trans
                i18nKey="recipientSuccessfullyRemoved"
                components={{ b: <b /> }}
                count={contactIds.length}
              />
            ),
            type: 'toast'
          });
        }
      }
    });
  };

  return (
    <S.GiftsWrapper>
      <CardContent>
        {editableContactList && (
          <S.AddingMethods>
            <S.AddingMethodsBody>
              <S.AddingMethodsTitle variant="body1Bold" color="fg">
                {t('sharingMethod')}:
              </S.AddingMethodsTitle>
              <S.AddingMethodsList>
                <S.AddingMethodsListItem
                  id={CONTACT_TYPE.list}
                  name="addingMethod"
                  value={CONTACT_TYPE.list}
                  label={t('sendEmailsWithSmartGift')}
                  checked={addingMethod === CONTACT_TYPE.list}
                  onChange={async () => {
                    setAddingMethod(CONTACT_TYPE.list);
                    await updateRecognition({
                      params: { recognitionCode },
                      body: {
                        contacts: {
                          type: 'list'
                        }
                      }
                    });

                    await recipientDelete({
                      code: recognitionCode
                    });
                  }}
                />
                <S.AddingMethodsListItem
                  id={CONTACT_TYPE.number}
                  name="addingMethod"
                  value={CONTACT_TYPE.number}
                  checked={addingMethod === CONTACT_TYPE.number}
                  label={t('shareWithLink')}
                  onChange={async () => {
                    setAddingMethod(CONTACT_TYPE.number);

                    await updateRecognition({
                      params: { recognitionCode },
                      body: {
                        contacts: {
                          type: 'number',
                          maxCount: contacts?.maxCount || 1
                        }
                      }
                    });

                    await recipientDelete({
                      code: recognitionCode
                    });
                  }}
                />
              </S.AddingMethodsList>
            </S.AddingMethodsBody>
          </S.AddingMethods>
        )}
        {addingMethods[contacts?.type]?.badge && (
          <Notification
            status="warning"
            style={{ marginBottom: 24 }}
            message={<Typography variant="body2">{addingMethods[contacts?.type].badge}</Typography>}
          />
        )}
        {addingMethod === CONTACT_TYPE.list ? (
          <>
            <DetailItem
              title={`${t('recipients')}:`}
              bodyStyle={{ alignItems: 'center', padding: '16px 0' }}
              className="item-d-flex"
            >
              {recognitionRecipients?.totalDocs || 0}
              {editableContactList && (
                <Button
                  variant="link"
                  onClick={() => {
                    setIsContactsEditModalOpen(true);
                  }}
                >
                  <Icon name="edit" px={14} color="var(--color-accent-primary-default)" />
                  {t('editRecipients')}
                </Button>
              )}
            </DetailItem>

            {isSuccessRecognition && addingMethod === CONTACT_TYPE.list && (
              <BasicRecipientTable
                ids={recognitionRecipientContactIds}
                recognitionCode={recognitionCode}
                actionButtons={{
                  delete: {
                    onClick: onDeleteModal
                  }
                }}
                onChangeSelectedRows={selectedRows => setSelectedRows(selectedRows)}
                onRemoveSelectedRows={onDeleteMultiModal}
              />
            )}

            {isContactsEditModalOpen && (
              <ContactsEditModal
                isCCPayment={recognition?.paymentMethod?.type === PAYMENT_METHOD_TYPES.creditCard}
                isModalOpen={isContactsEditModalOpen}
                onRequestClose={() => setIsContactsEditModalOpen(false)}
                selectedContactIds={recognitionRecipientContactIds || []}
                updateContacts={updateContacts}
              />
            )}
          </>
        ) : (
          <>
            <DetailItem
              editable={isDetailItemEditable}
              isCTADisabled={maxContactHasError}
              editButtonText={t('editRecipientLimit')}
              title={`${t('recipients')}: `}
              bodyStyle={{ alignItems: 'center', padding: '16px 0' }}
              closeWithoutSave={() => {
                setContacts(recognition?.contacts);
              }}
              saveDetail={() => {
                updateRecognition({
                  params: { recognitionCode },
                  body: {
                    contacts
                  },
                  successMessage: t('recipientsUpdatedSuccessfully')
                });
              }}
              editInput={() => (
                <Input
                  error={maxContactHasError}
                  value={contacts?.maxCount?.toString() || ''}
                  type="number"
                  label={`${t('recipientQuantity')}*`}
                  variant="outlined"
                  onKeyPress={event => {
                    if (!/[0-9]/.test(event.key)) {
                      event.preventDefault();
                    }
                  }}
                  onChange={event => {
                    setContacts({
                      ...contacts,
                      maxCount: event.target.value
                    });
                  }}
                />
              )}
            >
              {contacts?.maxCount}
            </DetailItem>
            <WithoutContactRecipientVerification
              recipientVerification={recognition?.settings?.recipientVerification}
              updateRecipientVerification={recipientVerification => {
                updateRecognition({
                  params: { recognitionCode },
                  body: {
                    settings: {
                      recipientVerification
                    }
                  }
                });
              }}
              recognitionCode={recognitionCode}
              recognitionMaxCount={recognition?.contacts?.maxCount}
            />
          </>
        )}
      </CardContent>
    </S.GiftsWrapper>
  );
};

export { ReviewAndPayContactsAccordion };
